import React, { useState } from "react";
import { Check } from "lucide-react";
import { PROCTORING_INSTRUCTIONS as DEFAULT_PROCTORING_INSTRUCTIONS } from "../../../constants/interviewInstructions";
import { useTheme } from "../../../hooks/useTheme.js";
import StepHeader from "./StepHeader";

// Proctoring guidelines on one page, closed by a single acknowledgement the
// candidate has to tick before moving on. Rows reuse the consent card's
// title/description styling.
const InstructionsStep = ({
  isTabSwitchScreenshotEnabled,
  handleShareScreen,
  onComplete,
  onBack,
  stepNumber,
  totalSteps,
  instructions = DEFAULT_PROCTORING_INSTRUCTIONS,
  completeLabel = "Next",
}) => {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [agreed, setAgreed] = useState(false);

  const cardClass = isLight
    ? "bg-white border-[#ECEDF5] shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
    : "bg-gray-800/50 border-gray-700/50 backdrop-blur-sm";
  const hairline = isLight ? "border-[#F1F2F7]" : "border-gray-700/50";

  // Screen-share prompt carried over from the paginated version, widened from
  // the cards on one page to the whole list.
  const handleNext = () => {
    if (!agreed) return;

    const needsScreenShare =
      isTabSwitchScreenshotEnabled &&
      instructions.some(
        (instruction) => instruction.title === "Screen Monitoring"
      );

    if (needsScreenShare) {
      handleShareScreen(onComplete);
    } else {
      onComplete();
    }
  };

  return (
    <div className="w-full">
      <StepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title="A few things to know"
        description="Please read through before you continue — you won't be able to review this once the interview starts."
      />

      <div className={`mt-7 rounded-2xl border ${cardClass}`}>
        <ul
          className={`px-6 divide-y ${
            isLight ? "divide-[#F1F2F7]" : "divide-gray-700/50"
          }`}
        >
          {instructions.map((instruction) => (
            <li key={instruction.title} className="py-4">
              <span
                className={`block text-[15px] font-normal leading-tight ${
                  isLight ? "text-slate-900" : "text-white"
                }`}
              >
                {instruction.title}
              </span>
              <span
                className={`block text-[12px] leading-relaxed mt-1.5 ${
                  isLight ? "text-slate-400" : "text-gray-400"
                }`}
              >
                {instruction.text}
              </span>
            </li>
          ))}
        </ul>

        <div className="px-6">
          <label
            className={`flex items-start gap-3.5 py-5 border-t cursor-pointer group ${hairline}`}
          >
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="sr-only"
            />
            <span
              aria-hidden="true"
              className={`w-[18px] h-[18px] mt-0.5 shrink-0 rounded-[5px] flex items-center justify-center transition-colors ${
                agreed
                  ? "bg-gradient-to-br from-[#2F64FF] to-[#FB54AE]"
                  : isLight
                  ? "bg-[#F1F2F7] border border-[#E1E3EC] group-hover:border-[#C9CCDA]"
                  : "bg-gray-700/60 border border-gray-600 group-hover:border-gray-500"
              }`}
            >
              {agreed && (
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              )}
            </span>

            <span
              className={`text-[15px] font-normal leading-tight ${
                isLight ? "text-slate-900" : "text-white"
              }`}
            >
              I understand and I will follow these instructions.
            </span>
          </label>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <button
          onClick={onBack}
          className={`px-7 py-2.5 rounded-full text-[14px] font-medium border transition-colors ${
            isLight
              ? "bg-white border-[#DDD8F0] text-slate-600 hover:bg-[#F7F7FC]"
              : "bg-transparent border-gray-600 text-gray-200 hover:bg-gray-700/40"
          }`}
        >
          Back
        </button>

        <button
          onClick={handleNext}
          disabled={!agreed}
          className={`px-7 py-2.5 rounded-full text-[14px] font-medium transition-opacity ${
            agreed
              ? "text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90"
              : "bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400"
          }`}
        >
          {completeLabel}
        </button>
      </div>
    </div>
  );
};

export default InstructionsStep;
